/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : video_content
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce fichier gère UNIQUEMENT les opérations CRUD sur la table `video_content`.
 * 
 * Pour les publications programmées, utiliser :
 * @see @/services/data/videoContentPublications.ts
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type VideoContent = Tables<"video_content">;
export type VideoContentInsert = TablesInsert<"video_content">;
export type VideoContentUpdate = TablesUpdate<"video_content">;

// Content types éditoriaux pour attribution automatique
const EDITORIAL_CONTENT_TYPES = [
  'mythbuster', 'hot_take', 'advice', 'storytelling',
  'liste', 'decryptage', 'reaction', 'profil',
] as const;

const VIRALITY_LEVELS = ['V1', 'V2', 'V3'] as const;

export const createVideoContent = async (video: VideoContentInsert) => {
  try {
    // Auto-assign content_type if not provided or default 'standard'
    const enrichedVideo = { ...video };
    if (!enrichedVideo.content_type || enrichedVideo.content_type === 'standard') {
      enrichedVideo.content_type = EDITORIAL_CONTENT_TYPES[
        Math.floor(Math.random() * EDITORIAL_CONTENT_TYPES.length)
      ];
    }
    // Auto-assign virality_predicted if not provided
    if (!enrichedVideo.virality_predicted) {
      enrichedVideo.virality_predicted = VIRALITY_LEVELS[
        Math.floor(Math.random() * VIRALITY_LEVELS.length)
      ];
    }

    const { data, error } = await supabase
      .from("video_content")
      .insert(enrichedVideo)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error creating video content:", error);
    return { success: false, error };
  }
};

export const getVideoContent = async (userId?: string) => {
  try {
    let query = supabase
      .from("video_content")
      .select("*")
      .order("created_at", { ascending: false });

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching video content:", error);
    return { success: false, error };
  }
};

export const updateVideoContent = async (id: string, updates: VideoContentUpdate) => {
  try {
    const { data, error } = await supabase
      .from("video_content")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating video content:", error);
    return { success: false, error };
  }
};

/**
 * Update the Instagram crosspost setting for a specific video content
 */
export const updateVideoContentInstagramCrosspost = async (
  id: string,
  disabled: boolean
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    const { error } = await supabase
      .from('video_content')
      .update({ instagram_crosspost_disabled: disabled })
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating instagram_crosspost_disabled:', error);
    return { success: false, error };
  }
};


export const updateVideoContentVersionEnabled = async (
  id: string,
  version: 'a' | 'b' | 'c',
  enabled: boolean
): Promise<{ success: boolean; error?: unknown }> => {
  const column = `version_${version}_enabled` as const;
  try {
    const { error } = await supabase
      .from('video_content')
      .update({ [column]: enabled })
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error(`Error updating ${column}:`, error);
    return { success: false, error };
  }
};

export const deleteVideoContent = async (id: string) => {
  try {
    const { error } = await supabase
      .from("video_content")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting video content:", error);
    return { success: false, error };
  }
};

export interface VideoContentWithPosts {
  id: string;
  user_id: string;
  title: string | null;
  description: string | null;
  thumbnail_url: string | null;
  video_1_thumbnail: string | null;
  video_2_thumbnail: string | null;
  video_3_thumbnail: string | null;
  is_prsnl_content: boolean | null;
  created_at: string;
  posts: Array<{
    id: number;
    platform: string | null;
    link: string | null;
    picture_link: string | null;
    posted_at: string | null;
    text: string | null;
  }>;
}

/**
 * Get video_content that haven't been classified yet (is_prsnl_content IS NULL)
 * with their linked social_posts
 */
export const getUnclassifiedVideoContent = async (options: {
  userId?: string;
  pageSize?: number;
  platform?: string;
}): Promise<{
  success: boolean;
  data: VideoContentWithPosts[];
  count: number;
  error?: unknown;
}> => {
  const { userId, pageSize = 1000, platform } = options;

  try {
    // If filtering by platform, we need to get video_content IDs that have posts on that platform first
    let videoContentIdsWithPlatform: string[] | null = null;
    
    if (platform) {
      const { data: platformPosts, error: platformError } = await supabase
        .from("social_posts")
        .select("video_content_id")
        .ilike("reseau_social", `%${platform}%`)
        .not("video_content_id", "is", null);
      
      if (platformError) throw platformError;
      
      videoContentIdsWithPlatform = [...new Set(platformPosts?.map(p => p.video_content_id).filter(Boolean) as string[])];
      
      if (videoContentIdsWithPlatform.length === 0) {
        return { success: true, data: [], count: 0 };
      }
    }

    // Get video_content that is unclassified (exclude multihooks as they are auto-classified)
    let query = supabase
      .from("video_content")
      .select("id, user_id, title, description, thumbnail_url, video_1_thumbnail, video_2_thumbnail, video_3_thumbnail, created_at", { count: "exact" })
      .is("is_prsnl_content", null)
      .or("is_multihook.is.null,is_multihook.eq.false")
      .order("created_at", { ascending: false })
      .limit(pageSize);

    if (userId) {
      query = query.eq("user_id", userId);
    }
    
    if (videoContentIdsWithPlatform) {
      query = query.in("id", videoContentIdsWithPlatform);
    }

    const { data: videoContents, error: vcError, count } = await query;
    if (vcError) throw vcError;

    if (!videoContents || videoContents.length === 0) {
      return { success: true, data: [], count: 0 };
    }

    // Get all linked posts for these video_contents
    const vcIds = (videoContents as Array<{ id: string }>).map(vc => vc.id);
    const { data: posts, error: postsError } = await supabase
      .from("social_posts")
      .select("id, video_content_id, reseau_social, link, picture_link, posted_at, text")
      .in("video_content_id", vcIds);

    if (postsError) throw postsError;

    // Group posts by video_content_id
    const postsByVcId = new Map<string, typeof posts>();
    for (const post of posts || []) {
      const vcId = post.video_content_id!;
      if (!postsByVcId.has(vcId)) {
        postsByVcId.set(vcId, []);
      }
      postsByVcId.get(vcId)!.push(post);
    }

    // Combine video_content with their posts
    const result: VideoContentWithPosts[] = (videoContents as Array<{
      id: string;
      user_id: string;
      title: string | null;
      description: string | null;
      thumbnail_url: string | null;
      video_1_thumbnail: string | null;
      video_2_thumbnail: string | null;
      video_3_thumbnail: string | null;
      created_at: string;
    }>).map(vc => ({
      ...vc,
      is_prsnl_content: null,
      posts: (postsByVcId.get(vc.id) || []).map(p => ({
        id: p.id,
        platform: p.reseau_social,
        link: p.link,
        picture_link: p.picture_link,
        posted_at: p.posted_at,
        text: p.text,
      })),
    }));

    return { success: true, data: result, count: count || 0 };
  } catch (error) {
    console.error("Error fetching unclassified video content:", error);
    return { success: false, data: [], count: 0, error };
  }
};

/**
 * Classified video content with aggregated platform info
 */
export interface ClassifiedVideoContent {
  id: string;
  user_id: string;
  title: string | null;
  thumbnail_url: string | null;
  is_prsnl_content: boolean;
  created_at: string;
  platforms: string[];
  post_count: number;
  posts: Array<{
    platform: string | null;
    link: string | null;
  }>;

  /**
   * Champs côté client uniquement (non persistés en DB)
   * - is_orphan: social_post sans video_content_id
   * - orphan_post_id: id du social_post source
   */
  is_orphan?: boolean;
  orphan_post_id?: number;
}

// Helper type for classified video content posts
type ClassifiedPost = { video_content_id: string | null; reseau_social: string | null; link: string | null };

/**
 * Fetches linked posts for video content IDs in chunks to avoid URL size limits
 */
const fetchLinkedPostsInChunks = async (vcIds: string[]): Promise<ClassifiedPost[]> => {
  const posts: ClassifiedPost[] = [];
  const CHUNK_SIZE = 200;

  for (let i = 0; i < vcIds.length; i += CHUNK_SIZE) {
    const chunk = vcIds.slice(i, i + CHUNK_SIZE);
    const { data: linkedPosts, error: postsError } = await supabase
      .from("social_posts")
      .select("video_content_id, reseau_social, link")
      .in("video_content_id", chunk);

    if (postsError) throw postsError;
    posts.push(...(linkedPosts || []));
  }

  return posts;
};

/**
 * Groups posts by video_content_id and aggregates platform info
 */
const groupPostsByVideoContentId = (posts: ClassifiedPost[]): Map<string, {
  platforms: Set<string>;
  count: number;
  posts: Array<{ platform: string | null; link: string | null }>;
}> => {
  const postsByVcId = new Map<string, {
    platforms: Set<string>;
    count: number;
    posts: Array<{ platform: string | null; link: string | null }>;
  }>();

  for (const post of posts) {
    const vcId = post.video_content_id;
    if (!vcId) continue;

    if (!postsByVcId.has(vcId)) {
      postsByVcId.set(vcId, { platforms: new Set(), count: 0, posts: [] });
    }
    const entry = postsByVcId.get(vcId)!;
    if (post.reseau_social) {
      entry.platforms.add(post.reseau_social);
    }
    entry.posts.push({ platform: post.reseau_social, link: post.link });
    entry.count++;
  }

  return postsByVcId;
};

/**
 * Get video_content that have been classified (is_prsnl_content IS NOT NULL)
 * with aggregated platform info
 */
export const getClassifiedVideoContent = async (options: {
  userId?: string;
  pageSize?: number;
  includeOrphanPosts?: boolean; // Deprecated: kept for backward compatibility but ignored
}): Promise<{
  success: boolean;
  prsnl: ClassifiedVideoContent[];
  other: ClassifiedVideoContent[];
  orphanCount?: number; // Always 0 - orphan concept removed
  error?: unknown;
}> => {
  const { userId, pageSize = 1000 } = options;
  const FAKE_USER_ID = 'd1f46b83-303a-4aec-a08c-750d31bbefac';

  try {
    // Get classified video_content
    let query = supabase
      .from("video_content")
      .select("id, user_id, title, thumbnail_url, is_prsnl_content, created_at")
      .not("is_prsnl_content", "is", null)
      .neq("user_id", FAKE_USER_ID)
      .order("created_at", { ascending: false })
      .limit(pageSize);

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data: videoContents, error: vcError } = await query;
    if (vcError) throw vcError;

    // Get all linked posts for these video_contents
    const vcIds = (videoContents as Array<{ id: string }>)?.map(vc => vc.id) || [];
    const posts = vcIds.length > 0 ? await fetchLinkedPostsInChunks(vcIds) : [];

    // Group posts by video_content_id
    const postsByVcId = groupPostsByVideoContentId(posts);

    // Build classified content with platform info
    const all: ClassifiedVideoContent[] = (videoContents as Array<{
      id: string;
      user_id: string;
      title: string | null;
      thumbnail_url: string | null;
      is_prsnl_content: boolean;
      created_at: string;
    }> || []).map(vc => {
      const postInfo = postsByVcId.get(vc.id) || { platforms: new Set(), count: 0, posts: [] };
      return {
        ...vc,
        platforms: Array.from(postInfo.platforms),
        post_count: postInfo.count,
        posts: postInfo.posts,
      };
    });

    const prsnl = all.filter(c => c.is_prsnl_content === true);
    const other = all.filter(c => c.is_prsnl_content === false);

    return { success: true, prsnl, other, orphanCount: 0 };
  } catch (error) {
    console.error("Error fetching classified video content:", error);
    return { success: false, prsnl: [], other: [], error };
  }
};

/**
 * Update the is_prsnl_content status of a video_content and its linked posts
 */
export const updateVideoContentPrsnlStatus = async (
  videoContentId: string,
  isPrsnl: boolean
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    // Update the video_content and return updated row to confirm write
    const { data: vcData, error: vcError } = await supabase
      .from("video_content")
      .update({ is_prsnl_content: isPrsnl })
      .eq("id", videoContentId)
      .select("id, is_prsnl_content")
      .single();

    if (vcError) throw vcError;
    
    // Verify write was successful
    if (vcData?.is_prsnl_content !== isPrsnl) {
      throw new Error(`Write verification failed: expected ${isPrsnl}, got ${vcData?.is_prsnl_content}`);
    }

    // Also update all linked social_posts
    const { error: postsError } = await supabase
      .from("social_posts")
      .update({ is_prsnl_content: isPrsnl })
      .eq("video_content_id", videoContentId);

    if (postsError) throw postsError;

    return { success: true };
  } catch (error) {
    console.error("Error updating video content PRSNL status:", error);
    return { success: false, error };
  }
};

/**
 * Reset is_prsnl_content to null on video_content and social_posts (for re-classification).
 * When userId is set, only that user's content is reset; otherwise all classified content is reset.
 */
export const resetPrsnlContentClassification = async (
  userId?: string
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    if (userId) {
      const { error: vcError } = await supabase
        .from("video_content")
        .update({ is_prsnl_content: null })
        .eq("user_id", userId);
      if (vcError) throw vcError;

      const { error: postsError } = await supabase
        .from("social_posts")
        .update({ is_prsnl_content: null })
        .eq("user_id", userId);
      if (postsError) throw postsError;
    } else {
      const { error: vcError } = await supabase
        .from("video_content")
        .update({ is_prsnl_content: null })
        .not("is_prsnl_content", "is", null);
      if (vcError) throw vcError;

      const { error: postsError } = await supabase
        .from("social_posts")
        .update({ is_prsnl_content: null })
        .not("is_prsnl_content", "is", null);
      if (postsError) throw postsError;
    }
    return { success: true };
  } catch (error) {
    console.error("Error resetting PRSNL classification:", error);
    return { success: false, error };
  }
};

export const getScheduledContentWithPlatforms = async (userId: string) => {
  try {
    // Get future publications with their channels
    const { data, error } = await supabase
      .from("video_content_publications")
      .select(`
        channel,
        scheduled_at,
        video_content!inner(user_id)
      `)
      .eq("video_content.user_id", userId)
      .gte("scheduled_at", new Date().toISOString());

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching scheduled content with platforms:", error);
    return { success: false, error, data: [] };
  }
};

/**
 * Récupère les contenus vidéo pour le calendrier
 * 
 * @param userId - ID de l'utilisateur
 * @returns {Promise<{success: boolean, data?: VideoContent[], error?: any}>}
 */
export const getVideoContentForCalendar = async (userId: string) => {
  try {
    // Fetch video content with their publications from the dedicated table
    // Include 'to_publish' (auto-validated) videos alongside regular statuses
    const { data, error } = await supabase
      .from("video_content")
      .select(`
        *,
        video_content_publications (
          id,
          channel,
          scheduled_at,
          status
        )
      `)
      .eq("user_id", userId)
      .not("status", "eq", "published")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching video content for calendar:", error);
    return { success: false, error, data: [] };
  }
};

export interface VideoContentWithPublications {
  id: string;
  title: string | null;
  created_at: string;
  shooting_id: string | null;
  virality_predicted: string | null;
  publications: Array<{
    id: string;
    channel: string;
    scheduled_at: string;
  }>;
}

/**
 * Backfill multihook content
 * @deprecated Use runBackfillMatching from contentMatching.ts instead
 * This function now delegates to the unified backfill-matching pipeline
 */
export const backfillMultihook = async (userId?: string): Promise<{
  success: boolean;
  data?: {
    success: boolean;
    videoContentsCreated?: number;
    videoContentsUpdated?: number;
    postsLinked?: number;
    error?: string;
  };
  error?: string;
}> => {
  try {
    // Delegate to the unified backfill-matching pipeline
    const { data, error } = await supabase.functions.invoke('backfill-matching', {
      body: { user_id: userId, dry_run: false },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    // Map response to legacy format
    return {
      success: true,
      data: {
        success: data?.success ?? true,
        videoContentsCreated: data?.orphanPostsVideoContentsCreated || 0,
        videoContentsUpdated: data?.multihookClassificationVideoContentsUpdated || 0,
        postsLinked: data?.identicalYoutubePostsLinked || 0,
      },
    };
  } catch (error) {
    console.error('Backfill multihook error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Backfill orphan posts
 * @deprecated Use runBackfillMatching from contentMatching.ts instead
 * This function now delegates to the unified backfill-matching pipeline
 */
export const backfillOrphanPosts = async (userId: string, dryRun: boolean = false): Promise<{
  success: boolean;
  data?: {
    success: boolean;
    video_contents_created?: number;
    posts_processed?: number;
    errors?: string[];
  };
  error?: string;
}> => {
  try {
    // Delegate to the unified backfill-matching pipeline
    const { data, error } = await supabase.functions.invoke('backfill-matching', {
      body: { user_id: userId, dry_run: dryRun },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    // Map response to legacy format
    return {
      success: true,
      data: {
        success: data?.success ?? true,
        video_contents_created: data?.orphanPostsVideoContentsCreated || 0,
        posts_processed: data?.orphanPostsProcessed || 0,
        errors: data?.errors || [],
      },
    };
  } catch (error) {
    console.error('Backfill orphan posts error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export const getVideoContentWithPublications = async (userId: string): Promise<VideoContentWithPublications[]> => {
  try {
    const { data: videos, error: videosError } = await supabase
      .from('video_content')
      .select('id, title, created_at, shooting_id, virality_predicted, status')
      .eq('user_id', userId)
      .neq('status', 'published')
      .order('created_at', { ascending: true });

    if (videosError || !videos) {
      console.error('Error fetching videos:', videosError);
      return [];
    }

    const videoIds = videos.map(v => v.id);
    const { data: publications, error: pubsError } = await supabase
      .from('video_content_publications')
      .select('id, video_content_id, channel, scheduled_at')
      .in('video_content_id', videoIds);

    if (pubsError) {
      console.error('Error fetching publications:', pubsError);
      return [];
    }

    return videos.map(video => ({
      ...video,
      publications: (publications || []).filter(p => p.video_content_id === video.id)
    }));
  } catch (error) {
    console.error('Error fetching video content with publications:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// AUTO-CLASSIFY HELPER TYPES AND FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

type VideoContentRow = { id: string; is_prsnl_content: boolean | null };
type PostRow = {
  id: number;
  video_content_id: string | null;
  type: string | null;
  reseau_social: string | null;
};
type PostInfo = { type: string | null; reseau_social: string | null };

const PAGE_SIZE = 1000;

/**
 * Yields control to the main thread to keep the UI responsive
 */
const yieldToMainThread = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Creates a conditional logger based on debug settings
 */
const createDebugLogger = (): ((...args: unknown[]) => void) => {
  const shouldLog =
    import.meta.env.DEV &&
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('debug_autoclassify') === '1';

  return (...args: unknown[]) => {
    if (shouldLog) console.log(...args);
  };
};

/**
 * Checks if a post is a video type
 */
const isVideoPost = (p: { type?: string | null; reseau_social?: string | null }): boolean => {
  const type = (p.type || '').toLowerCase();
  const platform = (p.reseau_social || '').toLowerCase();
  return (
    type.includes('video') ||
    type.includes('reel') ||
    type === 'short' ||
    type === 'shorts' ||
    platform === 'youtube' ||
    platform === 'tiktok'
  );
};

/**
 * Splits an array into chunks of specified size
 */
const chunkArray = <T>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, (i + 1) * size)
  );

/**
 * Fetches all video content rows with pagination
 */
const fetchAllVideoContent = async (userId?: string): Promise<VideoContentRow[]> => {
  const allContent: VideoContentRow[] = [];

  for (let offset = 0; ; offset += PAGE_SIZE) {
    let query = supabase.from('video_content').select('id, is_prsnl_content');

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;

    allContent.push(...(data as VideoContentRow[]));
    if (data.length < PAGE_SIZE) break;

    await yieldToMainThread();
  }

  return allContent;
};

/**
 * Fetches all social posts linked to video content with pagination
 */
const fetchAllLinkedPosts = async (userId?: string): Promise<PostRow[]> => {
  const allPosts: PostRow[] = [];

  for (let offset = 0; ; offset += PAGE_SIZE) {
    let query = supabase
      .from('social_posts')
      .select('id, video_content_id, type, reseau_social')
      .not('video_content_id', 'is', null);

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;

    allPosts.push(...(data as PostRow[]));
    if (data.length < PAGE_SIZE) break;

    await yieldToMainThread();
  }

  return allPosts;
};

/**
 * Groups posts by their video_content_id
 */
const groupPostsByContentId = async (
  posts: PostRow[]
): Promise<Map<string, PostInfo[]>> => {
  const postsByContentId = new Map<string, PostInfo[]>();

  let mapped = 0;
  for (const post of posts) {
    const contentId = post.video_content_id;
    if (!contentId) continue;

    const existingPosts = postsByContentId.get(contentId) || [];
    existingPosts.push({
      type: post.type,
      reseau_social: post.reseau_social,
    });
    postsByContentId.set(contentId, existingPosts);

    mapped++;
    if (mapped % 5000 === 0) {
      await yieldToMainThread();
    }
  }

  return postsByContentId;
};

/**
 * Classification result for a content
 */
type ClassificationResult = {
  id: string;
  targetValue: boolean;
};

/**
 * Checks if debug logging is enabled for auto-classify
 */
const isDebugLoggingEnabled = (): boolean =>
  import.meta.env.DEV &&
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('debug_autoclassify') === '1';

/**
 * Logs a sample reclassification entry for debugging
 */
const logReclassificationSample = (
  log: (...args: unknown[]) => void,
  vc: VideoContentRow,
  posts: PostInfo[],
  targetValue: boolean
): void => {
  const videoPosts = posts.filter(isVideoPost);
  const nonVideoPosts = posts.filter(p => !isVideoPost(p));
  log(`[AutoClassify] Content ${vc.id}: ${videoPosts.length} videos, ${nonVideoPosts.length} non-videos → ${targetValue ? 'PRSNL' : 'Autre'}`);
};

/**
 * Processes a single video content item for reclassification
 */
const processContentForReclassification = (
  vc: VideoContentRow,
  posts: PostInfo[],
  result: { toMarkAsPrsnl: string[]; toMarkAsAutre: string[] },
  log: (...args: unknown[]) => void,
  shouldLog: boolean,
  samplesLogged: { count: number }
): void => {
  const { shouldReclassify, targetValue } = determinePrsnlClassification(vc, posts);

  if (!shouldReclassify) return;

  if (shouldLog && samplesLogged.count < 5) {
    logReclassificationSample(log, vc, posts, targetValue);
    samplesLogged.count++;
  }

  if (targetValue) {
    result.toMarkAsPrsnl.push(vc.id);
  } else {
    result.toMarkAsAutre.push(vc.id);
  }
};

/**
 * Identifies video content that needs PRSNL reclassification (both directions)
 *
 * Returns two arrays:
 * - toMarkAsPrsnl: content IDs that should become is_prsnl_content = true
 * - toMarkAsAutre: content IDs that should become is_prsnl_content = false
 */
const identifyContentForReclassification = async (
  allContent: VideoContentRow[],
  postsByContentId: Map<string, PostInfo[]>,
  log: (...args: unknown[]) => void
): Promise<{ toMarkAsPrsnl: string[]; toMarkAsAutre: string[] }> => {
  const result = { toMarkAsPrsnl: [] as string[], toMarkAsAutre: [] as string[] };
  const shouldLog = isDebugLoggingEnabled();
  const samplesLogged = { count: 0 };

  for (let i = 0; i < allContent.length; i++) {
    const vc = allContent[i];
    const posts = postsByContentId.get(vc.id) || [];

    processContentForReclassification(vc, posts, result, log, shouldLog, samplesLogged);

    if ((i + 1) % 250 === 0) {
      await yieldToMainThread();
    }
  }

  return result;
};

/**
 * @deprecated Use identifyContentForReclassification instead
 * Kept for backward compatibility
 */
const identifyNonPureVideoContent = async (
  allContent: VideoContentRow[],
  postsByContentId: Map<string, PostInfo[]>,
  log: (...args: unknown[]) => void
): Promise<string[]> => {
  const { toMarkAsAutre } = await identifyContentForReclassification(allContent, postsByContentId, log);
  return toMarkAsAutre;
};

/**
 * Determines what PRSNL classification a video content should have.
 * 
 * PRSNL RULE:
 * - Must have at least 2 video-type posts (VIDEO, REEL, TIKTOK, YOUTUBE, SHORT)
 * - Must have 0 non-video posts (image, carousel, post)
 * 
 * @returns { shouldReclassify: boolean, targetValue: boolean } - Whether to reclassify and the target value
 */
const determinePrsnlClassification = (
  vc: VideoContentRow,
  posts: PostInfo[]
): { shouldReclassify: boolean; targetValue: boolean } => {
  // Count video and non-video posts
  const videoPosts = posts.filter(isVideoPost);
  const nonVideoPosts = posts.filter(p => !isVideoPost(p));
  
  // PRSNL = at least 2 videos AND 0 non-video posts
  const shouldBePrsnl = videoPosts.length >= 2 && nonVideoPosts.length === 0;
  
  // Check if current state matches what it should be
  if (shouldBePrsnl && vc.is_prsnl_content !== true) {
    return { shouldReclassify: true, targetValue: true }; // Should become PRSNL
  }
  if (!shouldBePrsnl && vc.is_prsnl_content !== false) {
    return { shouldReclassify: true, targetValue: false }; // Should become Autre
  }
  
  return { shouldReclassify: false, targetValue: vc.is_prsnl_content ?? false };
};

/**
 * @deprecated Use determinePrsnlClassification instead
 * Kept for backward compatibility - determines if content should become "Autre"
 */
const determineIfShouldReclassify = (
  vc: VideoContentRow,
  posts: PostInfo[]
): boolean => {
  const { shouldReclassify, targetValue } = determinePrsnlClassification(vc, posts);
  // Only return true if it should be reclassified to Autre (false)
  return shouldReclassify && targetValue === false;
};

/**
 * Updates the classification for the specified content IDs to a specific value
 */
const updateClassificationTo = async (contentIds: string[], isPrsnl: boolean): Promise<void> => {
  const CHUNK_SIZE = 100;
  const chunks = chunkArray(contentIds, CHUNK_SIZE);

  for (const ids of chunks) {
    const [{ error: updateContentError }, { error: updatePostsError }] = await Promise.all([
      supabase
        .from('video_content')
        .update({ is_prsnl_content: isPrsnl, updated_at: new Date().toISOString() })
        .in('id', ids),
      supabase.from('social_posts').update({ is_prsnl_content: isPrsnl }).in('video_content_id', ids),
    ]);

    if (updateContentError) throw updateContentError;
    if (updatePostsError) throw updatePostsError;

    await yieldToMainThread();
  }
};

/**
 * @deprecated Use updateClassificationTo instead
 * Kept for backward compatibility - marks content as "Autre" (false)
 */
const updateClassification = async (contentIds: string[]): Promise<void> => {
  await updateClassificationTo(contentIds, false);
};

/**
 * Auto-classify video content bidirectionally:
 * - Mark as PRSNL (true) if: >=2 video posts AND 0 non-video posts
 * - Mark as Autre (false) otherwise
 * 
 * PRSNL RULE:
 * - Must have at least 2 video-type posts (VIDEO, REEL, TIKTOK, YOUTUBE, SHORT)
 * - Must have 0 non-video posts (image, carousel, post)
 * 
 * Uses pagination to handle large datasets efficiently.
 */
export const autoClassifyVideoContent = async (userId?: string): Promise<{
  success: boolean;
  data?: { reclassifiedCount: number; toPrsnl?: number; toAutre?: number };
  error?: unknown;
}> => {
  try {
    const log = createDebugLogger();
    const userIdentifier = userId ? 'user ' + userId : 'ALL USERS';
    log('[AutoClassify] Running BIDIRECTIONAL classification for ' + userIdentifier);

    // Fetch all video content
    const allContent = await fetchAllVideoContent(userId);
    log('[AutoClassify] Found ' + allContent.length + ' video_content');

    // Fetch all linked posts
    const allPosts = await fetchAllLinkedPosts(userId);
    log('[AutoClassify] Found ' + allPosts.length + ' linked social_posts');

    // Group posts by content ID
    const postsByContentId = await groupPostsByContentId(allPosts);

    // Identify content to reclassify (both directions)
    const { toMarkAsPrsnl, toMarkAsAutre } = await identifyContentForReclassification(
      allContent,
      postsByContentId,
      log
    );

    log('[AutoClassify] ' + toMarkAsPrsnl.length + ' contents to mark as PRSNL');
    log('[AutoClassify] ' + toMarkAsAutre.length + ' contents to mark as Autre');

    const totalToReclassify = toMarkAsPrsnl.length + toMarkAsAutre.length;
    
    if (totalToReclassify === 0) {
      return { success: true, data: { reclassifiedCount: 0, toPrsnl: 0, toAutre: 0 } };
    }

    // Update classifications in database (both directions in parallel)
    await Promise.all([
      toMarkAsPrsnl.length > 0 ? updateClassificationTo(toMarkAsPrsnl, true) : Promise.resolve(),
      toMarkAsAutre.length > 0 ? updateClassificationTo(toMarkAsAutre, false) : Promise.resolve(),
    ]);

    return { 
      success: true, 
      data: { 
        reclassifiedCount: totalToReclassify,
        toPrsnl: toMarkAsPrsnl.length,
        toAutre: toMarkAsAutre.length,
      } 
    };
  } catch (error) {
    console.error('Error auto-classifying video content:', error);
    return { success: false, error };
  }
};
